import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthentificationService } from 'src/app/core/services/authentication.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import {
  USER_ACCOUNT_RECOVERY,
  USER_ACCOUNT_RECOVERY_FAILED,
  USER_ACCOUNT_RECOVERY_SUCCESS,
  USER_LOGIN,
  USER_LOGIN_FAILED,
  USER_LOGIN_SUCCESS,
  USER_REGISTRATION,
  USER_REGISTRATION_FAILED,
  USER_REGISTRATION_RESULT
} from '../actions/user-authentification.actions';
import { DialogService } from 'src/app/core/services/dialog.service';
import { Router } from '@angular/router';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { PreloaderService } from 'src/app/core/services/preloader.service';
import { ROUTES } from 'src/app/core/constants/routes';
import { GtmService } from 'src/app/core/services/gtm.service';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_LOGIN),
      tap(() => {
        this.spinnerService.showSpinner();
        this.preloaderService.triggerPreloader();
      }),
      switchMap((action) =>
        this.authService.login(action.username, action.password, action.rememberMe).pipe(
          map((response) => USER_LOGIN_SUCCESS({ loggedUser: response.data })),
          catchError((response) => of(USER_LOGIN_FAILED({ response: response })))
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_LOGIN_SUCCESS),
        tap((action) => {
          this.gtmService.push({
            event: 'login',
            country: 'rs',
            user_id: action.loggedUser.uuid
          });
          this.router.navigate([ROUTES.HOME]).then(() => {
            this.spinnerService.hideSpinner();
            this.preloaderService.hidePreloader();
          });
        })
      ),
    { dispatch: false }
  );

  authorizationFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_LOGIN_FAILED, USER_REGISTRATION_FAILED),
        tap(() => {
          this.spinnerService.hideSpinner();
        })
      ),
    { dispatch: false }
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_REGISTRATION),
      switchMap((action) =>
        this.authService.register(action.registeredUser).pipe(
          map((response) => USER_REGISTRATION_RESULT({ response: response })),
          catchError((response) => of(USER_REGISTRATION_FAILED({ response: response })))
        )
      )
    )
  );

  registerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_REGISTRATION_RESULT),
        tap((action) => {
          this.spinnerService.hideSpinner();
          if (action.response.ibetResponseStatus == 'OK') {
            this.gtmService.push({ event: 'registration_step1', country: 'rs' });
            this.spinnerService.hideSpinner();
            this.router.navigate([ROUTES.VERIFICATION]);
          }
        })
      ),
    { dispatch: false }
  );

  recoveryUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_ACCOUNT_RECOVERY),
      switchMap((action) =>
        this.authService.recover(action.email).pipe(
          map((message) => USER_ACCOUNT_RECOVERY_SUCCESS({ message })),
          catchError((response) =>
            of(USER_ACCOUNT_RECOVERY_FAILED({ message: response.error.message }))
          )
        )
      )
    )
  );

  recoveryUserAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_ACCOUNT_RECOVERY_SUCCESS),
        tap(() => {
          this.spinnerService.hideSpinner();
          this.router.navigateByUrl(ROUTES.PASSWORD_RECOVERY_SUBMIT);
        })
      ),
    { dispatch: false }
  );

  recoveryUserAccountFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_ACCOUNT_RECOVERY_FAILED),
        tap((action) => {
          this.spinnerService.hideSpinner();
          this.dialogService.openError('Greška!', action.message);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private dialogService: DialogService,
    private authService: AuthentificationService,
    private router: Router,
    private spinnerService: SpinnerService,
    private preloaderService: PreloaderService,
    private gtmService: GtmService
  ) {}
}
